import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, SlidersHorizontal, Zap } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { pedirSalida } from '@/lib/salida-con-cambios';
import ReglasPage from '@/features/pricing-rules/pages/reglas-page';
import { pricingRulesApi } from '@/features/pricing-rules/services/pricing-rules-api';
import { AppliedStrategiesTab } from '@/features/smart-pricing/components/applied-strategies-tab';
import { strategyApplicationsApi } from '@/features/smart-pricing/services/strategy-applications-api';
import { DestinoProductos } from '@/features/smart-pricing/components/destino-productos';
import { VueltaSheet } from '@/features/smart-pricing/components/vuelta-sheet';
import { MarcoCompite } from '../ui/marco-compite';
import { EncabezadoCompite } from '../ui/encabezado-compite';
import { PestanasCompite, type PestanaCompite } from '../ui/pestanas-compite';
import { SegmentosCompite } from '../ui/segmentos-compite';
import { AvisoCambiosNuevos } from '../components/aviso-cambios-nuevos';
import { AyudaEstrategias } from '../components/ayuda-estrategias';
import { AvisoDemo, AVISO_ESTRATEGIA_DEMO } from '@/features/mini-prixia/components/aviso-demo';
import {
  esVistaDeEstrategia,
  useOrquestacionCompite,
  usePermisosDeCompite,
  type VistaDeEstrategia,
} from '../hooks/use-orquestacion-compite';

/**
 * Compite › Estrategia (Configuración, en la maqueta): las reglas que limitan
 * los precios y las estrategias que los proponen. Dos pestañas, como la maqueta.
 * «Cómo queda cada producto» (la pestaña Productos de antes) vive adentro de
 * Reglas, en «Las reglas | Cómo queda cada producto»: es lo que hacen las reglas
 * con cada producto, y el «Productos» de la maqueta es el catálogo (de Jordi).
 * La URL sigue igual (`?tab=reglas|estrategias|productos`; `productos` abre
 * Reglas en esa vista, así los enlaces de antes y el «Ver cuáles» de Decisiones
 * llegan igual; el `?ver=` de antes también se entiende).
 *
 * Como la maqueta: Reglas primero (y abierta por omisión), el botón principal de
 * cada pestaña a la derecha del encabezado («Nueva regla» / «Crear una
 * estrategia») y, debajo de las pestañas, una línea de ayuda con «¿Cómo
 * funcionan?».
 *
 * Permisos, sin cambiar los de antes: Estrategias pide el módulo; Reglas (y
 * cómo queda cada producto), ver reglas. Quien sólo tiene el módulo entra a Estrategias.
 */
export default function EstrategiaPage() {
  const { veEstrategias, veReglas } = usePermisosDeCompite();
  const o = useOrquestacionCompite();
  const [params, setParams] = useSearchParams();
  const [cuentas, setCuentas] = useState<Partial<Record<VistaDeEstrategia, number>>>({});
  // El lado derecho del encabezado: cada pestaña pone ahí su botón principal.
  const [destinoDeLaAccion, setDestinoDeLaAccion] = useState<HTMLDivElement | null>(null);

  const disponible = (v: VistaDeEstrategia) => (v === 'estrategias' ? veEstrategias : veReglas);
  const pedida = params.get('tab') ?? params.get('ver');
  const porOmision: VistaDeEstrategia = veReglas ? 'reglas' : 'estrategias';
  const vista: VistaDeEstrategia =
    esVistaDeEstrategia(pedida) && disponible(pedida) ? pedida : porOmision;

  const cambiar = (v: VistaDeEstrategia) => {
    if (v === vista) return;
    // Spec 294: salir de Reglas con cambios propios sin aplicar pregunta antes.
    pedirSalida(() =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('tab', v);
          p.delete('ver');
          // `?filtro=sin-costo` y `?vuelta=` sirven para llegar una vez, no para quedarse.
          p.delete('filtro');
          p.delete('vuelta');
          return p;
        },
        { replace: true }
      )
    );
  };

  // Estables (sin dependencias): los hijos los tienen en sus efectos, y una
  // función nueva en cada dibujo los haría avisar de más.
  const contarEstrategias = useCallback(
    (n: number) => setCuentas((c) => (c.estrategias === n ? c : { ...c, estrategias: n })),
    []
  );
  const contarReglas = useCallback(
    (n: number) => setCuentas((c) => (c.reglas === n ? c : { ...c, reglas: n })),
    []
  );

  // Las cuentas de las pestañas desde que se entra (la maqueta: «Reglas 9 ·
  // Estrategias 3»), no recién al visitar cada una. Sólo llenan lo que falta: la
  // pestaña abierta informa la suya al cargar su lista, y ésa manda. Si el
  // pedido falla, la pestaña va sin número, como antes.
  useEffect(() => {
    let vivo = true;
    const siFalta = (clave: VistaDeEstrategia, n: number) =>
      vivo && setCuentas((c) => (c[clave] === undefined ? { ...c, [clave]: n } : c));
    if (veEstrategias)
      strategyApplicationsApi
        .listar()
        .then((l) => siFalta('estrategias', l.length))
        .catch(() => undefined);
    if (veReglas)
      pricingRulesApi
        .list()
        .then((l) => siFalta('reglas', l.length))
        .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [veEstrategias, veReglas]);

  const pestanas: Array<PestanaCompite<'reglas' | 'estrategias'>> = [
    ...(veReglas
      ? [{ valor: 'reglas' as const, texto: 'Reglas', cuenta: cuentas.reglas, icono: ShieldCheck }]
      : []),
    ...(veEstrategias
      ? [
          {
            valor: 'estrategias' as const,
            texto: 'Estrategias',
            cuenta: cuentas.estrategias,
            icono: Zap,
          },
        ]
      : []),
  ];
  // «Cómo queda cada producto» es una vista de Reglas: la pestaña marcada es Reglas.
  const pestana = vista === 'estrategias' ? 'estrategias' : 'reglas';

  return (
    <AppLayout>
      <MarcoCompite>
        <EncabezadoCompite
          seccion="Estrategia"
          icono={SlidersHorizontal}
          titulo="Estrategia"
          acciones={
            <div
              ref={setDestinoDeLaAccion}
              data-tour="demo-estrategia-accion"
              className="flex items-center gap-2"
            />
          }
        />
        <AvisoDemo texto={AVISO_ESTRATEGIA_DEMO} tour="demo-estrategia-aviso" />
        <PestanasCompite
          etiqueta="Qué querés configurar"
          pestanas={pestanas}
          valor={pestana}
          onCambiar={cambiar}
        />
        {o.hayCambiosNuevos && vista === 'estrategias' && (
          <AvisoCambiosNuevos onRecargar={o.recargar} />
        )}
        <div
          role="tabpanel"
          aria-labelledby={`pestana-${pestana}`}
          data-tour="demo-estrategia-contenido"
          className="space-y-5"
        >
          {pestana === 'reglas' && veReglas && (
            <SegmentosCompite
              aria-label="Qué ver de las reglas"
              opciones={[
                { valor: 'reglas', texto: 'Las reglas' },
                { valor: 'productos', texto: 'Cómo queda cada producto' },
              ]}
              valor={vista === 'productos' ? 'productos' : 'reglas'}
              onCambiar={cambiar}
            />
          )}
          {vista === 'reglas' && veReglas && (
            <ReglasPage
              embebida
              enCompite
              destinoDeLaAccion={destinoDeLaAccion}
              // Después de recalcular, a Revisar precios sin filtro de origen (spec 294, D-R4-3).
              onIrAPorDecidirDelRecalculo={() => o.irA('BANDEJA', null)}
              onTotal={contarReglas}
            />
          )}
          {vista === 'estrategias' && veEstrategias && (
            <>
              <AyudaEstrategias />
              <AppliedStrategiesTab
                onIrA={o.irA}
                refreshKey={o.refreshKey}
                onTotal={contarEstrategias}
                enCompite
                destinoDeLaAccion={destinoDeLaAccion}
              />
            </>
          )}
          {vista === 'productos' && veReglas && <DestinoProductos />}
        </div>
      </MarcoCompite>
      {veEstrategias && (
        <VueltaSheet
          strategyApplicationId={o.vuelta}
          onClose={() => o.abrirVuelta(null)}
          onIrA={o.irA}
          onResuelto={o.alResolver}
        />
      )}
    </AppLayout>
  );
}
